/**
 * CR-8252: send one onboarding questionnaire to many approved suppliers.
 *
 * Opened from a questionnaire card in the library (questionnaire given), or
 * from Questionnaires › Supplier Responses (the user picks the questionnaire
 * first; R5 option A, Aaron 27 Oct 2026). Lists the suppliers approved at the
 * user's sites; one that already holds the questionnaire (any version) is
 * shown but cannot be picked.
 */
import { useEffect, useMemo, useState } from 'react';
import { onboardingAPI, type AssignableSupplier, type OnboardingDataset } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Search, Send } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface SendQuestionnaireDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The questionnaire to send; omitted, the user picks a published one first. */
  dataset?: OnboardingDataset | null;
  onOpenChange: (open: boolean) => void;
  /** Called after a send that created at least one questionnaire. */
  onSent?: () => void;
}

export default function SendQuestionnaireDialog({ open, dataset: givenDataset, onOpenChange, onSent }: SendQuestionnaireDialogProps) {
  const choosing = givenDataset === undefined;
  const [choices, setChoices] = useState<OnboardingDataset[]>([]);
  const [chosenId, setChosenId] = useState<string>('');
  const dataset = choosing ? (choices.find((d) => String(d.id) === chosenId) ?? null) : givenDataset;
  const [suppliers, setSuppliers] = useState<AssignableSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !choosing) return;
    setChosenId('');
    onboardingAPI.getDatasets({ is_active: true, page_size: 100 })
      .then((res) => setChoices((res.data.results || []).filter((d) => d.is_active && d.is_published)))
      .catch((err: unknown) => setError(getApiErrorMessage(err, 'Could not load questionnaires.')));
  }, [open, choosing]);

  useEffect(() => {
    setQuery('');
    setPicked(new Set());
    setSuppliers([]);
    setError(null);
    if (!open || !dataset) return;
    setLoading(true);
    onboardingAPI.getAssignableSuppliers(dataset.id)
      .then((res) => setSuppliers(res.data.results))
      .catch((err: unknown) => setError(getApiErrorMessage(err, 'Could not load suppliers.')))
      .finally(() => setLoading(false));
  }, [open, dataset]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? suppliers.filter((s) => s.name.toLowerCase().includes(q)) : suppliers;
  }, [suppliers, query]);
  const pickable = shown.filter((s) => !s.already_has);
  const allShownPicked = pickable.length > 0 && pickable.every((s) => picked.has(s.id));
  const holdingCount = suppliers.filter((s) => s.already_has).length;

  const toggle = (id: number) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllShown = () => {
    setPicked((prev) => {
      const next = new Set(prev);
      for (const s of pickable) {
        if (allShownPicked) next.delete(s.id); else next.add(s.id);
      }
      return next;
    });
  };

  const handleSend = async () => {
    if (!dataset || picked.size === 0) return;
    setSending(true);
    try {
      const res = await onboardingAPI.assignQuestionnaire(dataset.id, Array.from(picked));
      const { created_count: sent, skipped_count: skipped } = res.data;
      toast.success(
        `Sent "${dataset.name}" to ${sent} supplier${sent === 1 ? '' : 's'}`
        + (skipped ? `; ${skipped} already had it` : ''),
      );
      onOpenChange(false);
      if (sent > 0) onSent?.();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not send the questionnaire.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{choosing ? 'Assign Questionnaire' : 'Send to approved suppliers'}</DialogTitle>
          <DialogDescription>
            {dataset
              ? `Each supplier you pick gets "${dataset.name}" to complete, and you review their answers.`
              : 'Pick a questionnaire, then the approved suppliers to send it to.'}
          </DialogDescription>
        </DialogHeader>

        {choosing && (
          <div className="space-y-2">
            <Label htmlFor="send-questionnaire-select">Questionnaire</Label>
            {choices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No published questionnaires yet. Publish one in Supplier Onboarding first.
              </p>
            ) : (
              <Select value={chosenId} onValueChange={setChosenId}>
                <SelectTrigger id="send-questionnaire-select">
                  <SelectValue placeholder="Select a questionnaire..." />
                </SelectTrigger>
                <SelectContent>
                  {choices.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {!dataset ? null : loading ? (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading suppliers…
          </div>
        ) : error ? (
          <p className="py-6 text-sm text-destructive">{error}</p>
        ) : suppliers.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">
            No approved suppliers at your sites yet. Suppliers appear here once they are approved.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search suppliers"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search suppliers"
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={allShownPicked}
                  onCheckedChange={toggleAllShown}
                  disabled={pickable.length === 0}
                  aria-label="Select all shown"
                />
                Select all{query ? ' shown' : ''}
              </label>
              {holdingCount > 0 && (
                <span className="text-muted-foreground">{holdingCount} already have it</span>
              )}
            </div>
            <ul className="max-h-72 overflow-y-auto divide-y rounded-md border">
              {shown.map((s) => (
                <li key={s.id}>
                  <label
                    className={`flex items-center gap-3 px-3 py-2 text-sm ${
                      s.already_has ? 'text-muted-foreground' : 'cursor-pointer hover:bg-muted/40'
                    }`}
                  >
                    <Checkbox
                      checked={picked.has(s.id)}
                      onCheckedChange={() => toggle(s.id)}
                      disabled={s.already_has}
                      aria-label={s.name}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-medium">{s.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">{s.sites.join(', ')}</span>
                    </span>
                    {s.already_has && <span className="text-xs">Already has it</span>}
                  </label>
                </li>
              ))}
              {shown.length === 0 && (
                <li className="px-3 py-4 text-sm text-muted-foreground">No supplier matches "{query}".</li>
              )}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={picked.size === 0 || sending}>
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            {picked.size === 0 ? 'Send' : `Send to ${picked.size} supplier${picked.size === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
